import { api, ApiError, buildAuthHeaders } from './client';
import { REPLY_DID_NOT_ARRIVE_MESSAGE } from '../utils/betaAllowance';
import { noteBeta402 } from '../utils/betaSpendPause';
import { SIGN_IN_EXPIRED } from '../utils/writerCopy';

// Longer than the server's first-token limit (120s) and its silence
// limit, so a live read is not given up while that generation is still
// the one running. If nothing arrives, the screen stops waiting.
export const STREAM_READ_IDLE_MS = 130000;

const API_BASE = process.env.REACT_APP_API_URL || '/api';

/**
 * Split a raw SSE buffer into complete events and leftover bytes.
 * Exported for unit tests.
 */
export function parseSseBlocks(buffer) {
  const events = [];
  const parts = buffer.split('\n\n');
  const rest = parts.pop() ?? '';
  for (const part of parts) {
    if (!part.trim()) continue;
    let event = 'message';
    const dataLines = [];
    for (const line of part.split('\n')) {
      if (line.startsWith('event:')) event = line.slice(6).trim();
      else if (line.startsWith('data:')) dataLines.push(line.slice(5).trimStart());
    }
    if (!dataLines.length) continue;
    let data;
    try {
      data = JSON.parse(dataLines.join('\n'));
    } catch {
      events.push({ event, data: null, malformed: true });
      continue;
    }
    events.push({ event, data });
  }
  return { events, rest };
}

async function throwApiError(response) {
  let data = {};
  const text = await response.text();
  if (text) {
    try {
      data = JSON.parse(text);
    } catch {
      data = {};
    }
  }
  const apiError = new ApiError(
    data?.error?.message || data?.detail || 'Request failed',
    data?.error?.code,
    response.status,
    data?.error
  );
  apiError.provisioned = Boolean(data?.error?.provisioned);
  if (response.status === 402) noteBeta402(data?.error);
  if (response.status === 409 && /still being written/i.test(apiError.message || '')) {
    apiError.code = 'reply_in_flight';
  }
  throw apiError;
}

// Matches backend/api/chat.py's NUDGE_MARKER_MESSAGE exactly — the fixed
// content a Nudge press persists (FQ1's visible, auditable marker). Message
// rendering (ChatMessage.jsx) matches on this content to style the marker
// as a small divider rather than a normal chat bubble.
export const NUDGE_MARKER_MESSAGE = 'Nudge requested';

export const chatApi = {
  /**
   * Send a message to the chat session.
   * @param {string} sessionId - Current session ID
   * @param {string} content - Message content
   * @param {string|null} previousSessionId - Previous session ID for summary trigger
   * @param {string|null} workingStyle - User's working style preference (explorer/architect)
   * @returns {Promise} Response with user_message, assistant_message, citation_map, session_status, continued_in
   */
  sendMessage: (sessionId, content, previousSessionId = null, workingStyle = null) => {
    console.log(`[CHAT DEBUG] Sending message to session: ${sessionId}`);
    const headers = {};
    if (previousSessionId) {
      headers['X-Previous-Session-Id'] = previousSessionId;
    }
    if (workingStyle) {
      headers['X-Working-Style'] = workingStyle;
    }
    return api.post(`/sessions/${sessionId}/messages`, { content }, { headers });
  },

  /**
   * Stream a writer-facing reply (Discovery / Explore / Focus / WorkshopChat).
   * Asks for SSE; mute and pre-first-chunk errors still come back as JSON.
   * @param {function} [options.onDelta] called with each token string
   * @returns {Promise<object>} ChatResponse fields plus streamed/interrupted
   */
  sendMessageStream: async (
    sessionId,
    content,
    previousSessionId = null,
    workingStyle = null,
    {
      onDelta,
      jumpStart = false,
      nudge = false,
      step0Answer = false,
      idleMs: idleMsOption,
      focusSectionId = null,
      retryMessageId = null,
    } = {}
  ) => {
    const extra = { Accept: 'text/event-stream' };
    if (previousSessionId) extra['X-Previous-Session-Id'] = previousSessionId;
    if (workingStyle) extra['X-Working-Style'] = workingStyle;

    const body = { content: content ?? '' };
    if (jumpStart) body.jump_start = true;
    if (nudge) body.nudge = true;
    if (step0Answer) body.step0_answer = true;
    if (focusSectionId) body.focus_section_id = focusSectionId;
    if (retryMessageId) body.retry_message_id = retryMessageId;

    const openStream = (headers) => fetch(`${API_BASE}/sessions/${sessionId}/messages`, {
      method: 'POST',
      headers,
      body: JSON.stringify(body),
    });

    // One fresh sign-in if auth rejects the opening response. A 401 here
    // means the route did not run, so the retry is not a second paid turn.
    // Once any bytes are read, a later failure is not retried.
    let response = await openStream(await buildAuthHeaders(extra));
    if (response.status === 401) {
      response = await openStream(await buildAuthHeaders(extra, { fresh: true }));
    }
    if (response.status === 401) {
      throw new ApiError(SIGN_IN_EXPIRED, 'UNAUTHORIZED', 401);
    }
    if (response.status === 402) {
      window.dispatchEvent(new Event('openrouter:balance-changed'));
    }
    if (!response.ok) {
      await throwApiError(response);
    }

    const contentType = response.headers.get('content-type') || '';
    if (!contentType.includes('text/event-stream')) {
      const text = await response.text();
      const data = text ? JSON.parse(text) : {};
      window.dispatchEvent(new Event('openrouter:balance-changed'));
      return { ...data, streamed: false, interrupted: false };
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';
    let result = null;
    let interrupted = false;
    let finished = false;
    const idleMs = Number.isFinite(idleMsOption) ? idleMsOption : STREAM_READ_IDLE_MS;

    const readNext = () => {
      let timer;
      const timeout = new Promise((_, reject) => {
        timer = setTimeout(() => {
          const stalled = new ApiError(REPLY_DID_NOT_ARRIVE_MESSAGE, 'LLM_ERROR', 504);
          stalled.stalled = true;
          reader.cancel().catch(() => {});
          reject(stalled);
        }, idleMs);
      });
      return Promise.race([
        reader.read().then(
          (value) => {
            clearTimeout(timer);
            return value;
          },
          (err) => {
            clearTimeout(timer);
            throw err;
          },
        ),
        timeout,
      ]);
    };

    const applyEvents = (raw) => {
      const parsed = parseSseBlocks(raw);
      for (const { event, data, malformed } of parsed.events) {
        const payload = data && typeof data === 'object' ? data : null;
        if (malformed || (data != null && payload == null && event !== 'delta')) {
          const failed = new ApiError(REPLY_DID_NOT_ARRIVE_MESSAGE, 'LLM_ERROR', 500);
          failed.stalled = true;
          throw failed;
        }
        if (event === 'delta' && typeof payload?.text === 'string' && payload.text) {
          onDelta?.(payload.text);
        } else if (event === 'done' && payload) {
          result = payload;
          finished = true;
        } else if (event === 'interrupted' && payload) {
          result = payload;
          interrupted = true;
          finished = true;
        } else if (event === 'error') {
          const failed = new ApiError(
            payload?.message || REPLY_DID_NOT_ARRIVE_MESSAGE,
            payload?.code || 'LLM_ERROR',
            500
          );
          if (!payload?.message) failed.stalled = true;
          throw failed;
        }
      }
      return parsed.rest;
    };

    // Return as soon as done/interrupted lands. Waiting for the socket to
    // close left Discovery on an empty typing bubble with input locked
    // while the reply was already saved (#947).
    while (!finished) {
      const { done, value } = await readNext();
      if (done) {
        buffer += decoder.decode();
        if (buffer.trim() && !buffer.endsWith('\n\n')) {
          buffer += '\n\n';
        }
        buffer = applyEvents(buffer);
        break;
      }
      buffer += decoder.decode(value, { stream: true });
      buffer = applyEvents(buffer);
    }

    if (finished) {
      try {
        await reader.cancel();
      } catch {
        // Already closed.
      }
    }

    if (!result) {
      const stalled = new ApiError(REPLY_DID_NOT_ARRIVE_MESSAGE, 'LLM_ERROR', 500);
      stalled.stalled = true;
      throw stalled;
    }

    window.dispatchEvent(new Event('openrouter:balance-changed'));
    return { ...result, streamed: true, interrupted };
  },

  /**
   * Trigger a Jump Start for the current session.
   * Sends jump_start=true with empty content. The response renders as a
   * normal assistant message; no user message is created in the DB.
   */
  jumpStart: (sessionId, previousSessionId = null, workingStyle = null) => {
    const headers = {};
    if (previousSessionId) {
      headers['X-Previous-Session-Id'] = previousSessionId;
    }
    if (workingStyle) {
      headers['X-Working-Style'] = workingStyle;
    }
    return api.post(
      `/sessions/${sessionId}/messages`,
      { content: '', jump_start: true },
      { headers }
    );
  },

  /**
   * Trigger a Nudge for the current session (nudge-button-spec.md).
   * Sends nudge=true with empty content — mid-session only, unlike Jump
   * Start. Unlike Jump Start's button, this DOES create a small visible
   * user message in the DB (the "Nudge requested" marker, FQ1) alongside
   * the assistant's direction batch.
   */
  nudge: (sessionId, previousSessionId = null, workingStyle = null) => {
    const headers = {};
    if (previousSessionId) {
      headers['X-Previous-Session-Id'] = previousSessionId;
    }
    if (workingStyle) {
      headers['X-Working-Style'] = workingStyle;
    }
    return api.post(
      `/sessions/${sessionId}/messages`,
      { content: '', nudge: true },
      { headers }
    );
  },

  /**
   * Regenerate the most recent assistant message in place. Backend rejects
   * the request (409) if messageId is no longer the session's last message.
   * @returns {Promise} Response with assistant_message (new content), citation_map, session_status
   */
  regenerateMessage: (sessionId, messageId) =>
    api.post(`/sessions/${sessionId}/messages/${messageId}/regenerate`, {}),

  /**
   * Get message history (issue #202: keyset pagination).
   * Defaults to the most-recent `limit` messages, chronological (oldest first).
   * Pass `cursor` (a prior response's `next_cursor`) to load the next-older batch.
   * @returns {Promise} { messages, has_more, next_cursor }
   */
  getMessages: (sessionId, { limit = 50, cursor = null } = {}) => {
    const params = new URLSearchParams({ limit: String(limit) });
    if (cursor) params.set('cursor', cursor);
    // Side Door return remounts Discovery talk. Do not reuse a first-visit
    // empty GET body if the sitting now has history (#959).
    return api.get(`/sessions/${sessionId}/messages?${params.toString()}`, {
      cache: 'no-store',
    });
  },

  /**
   * Get message details by ID.
   * Used for citation navigation - returns session_id to navigate to source.
   * @param {string} messageId - Message ID
   * @returns {Promise} Message details including session_id
   */
  getMessageDetails: (messageId) =>
    api.get(`/sessions/messages/${messageId}`),

  /**
   * Save / Dismiss / clear one Nudge idea (#2682).
   * Stored votes stay 'up' (Saved) and 'down' (Dismissed).
   * @param {string} vote - 'up' | 'down' | null
   */
  voteNudgeItem: (sessionId, messageId, itemId, vote, extra = {}) =>
    api.patch(`/sessions/${sessionId}/messages/${messageId}/nudge-votes`, {
      item_id: itemId,
      vote,
      ...(extra.removeCapture ? { remove_capture: true } : {}),
      ...(extra.openedSessionId ? {
        opened_session_id: extra.openedSessionId,
        opened_session_name: extra.openedSessionName || '',
      } : {}),
    }),
};
